import React from 'react';

import { PermissionsButtonWrapper } from '~/components/core';
import MenuItem from '~/components/core/Atomic/MenuItem';
import { PERMISSION_ACTIONS, PERMISSION_VERBS } from '~/components/core/Permissions/PermissionUtils';
import { useCms } from '~/components/hooks/useCms';
import CONFIGURATION_FEATURES from '~/server_shared/generated-types/CONFIGURATION_FEATURES';
import { isFeatureEnabled } from '~/Utils';

import { useRecoveryRequestsClaimConfig } from './useRecoveryRequests';

interface SendRecoveryRequestMenuItemProps {
  onClick: () => void;
  disabled?: boolean;
}

/**
 * The exposure 3-dots entry that opens the send dialog, and the gate in front of it.
 *
 * Gating lives here rather than in the container for the same reason it lives in the two items
 * either side of it: the container renders the menu, not the decisions behind each row.
 *
 * **Absence and refusal are different answers, and they render differently.** A non-pilot
 * organization, or a claim the configured provider cannot serve, has no such capability at all --
 * there is nothing to explain, so no row is rendered. A missing permission is the opposite: the
 * capability exists here and *this user* may not use it, which is worth telling them rather than
 * leaving them to read absence as "the feature is not installed". `PermissionsButtonWrapper` is how
 * every other permission-gated action in the app says so -- disabled, with the standard reason on
 * hover -- and it resolves the permission itself, so no second check here can drift from it.
 *
 * The claim read is therefore gated on the feature flag alone. It used to require the write verb
 * too, which cost nothing while a user without it saw no row; now that such a user sees a disabled
 * one, the answer deciding whether the row exists at all has to be fetched for them too. The flag
 * still short-circuits it for every organization outside the pilot, and the endpoint behind it
 * needs only CLAIM:READ -- the permission that rendered this claim in the first place.
 */
const SendRecoveryRequestMenuItem: React.FC<SendRecoveryRequestMenuItemProps> = ({ onClick, disabled }) => {
  const { userOrganization } = useCms();
  const isRecoveryRequestsEnabled = isFeatureEnabled(userOrganization, CONFIGURATION_FEATURES.RECOVERY_REQUESTS.name);
  const { data: config } = useRecoveryRequestsClaimConfig({ shouldFetch: isRecoveryRequestsEnabled });

  if (!isRecoveryRequestsEnabled || !config?.is_enabled) {
    return null;
  }

  return (
    <PermissionsButtonWrapper action={PERMISSION_ACTIONS.RECOVERY_REQUEST} verb={PERMISSION_VERBS.WRITE}>
      <MenuItem onClick={onClick} disabled={disabled}>
        Send recovery request
      </MenuItem>
    </PermissionsButtonWrapper>
  );
};

export default SendRecoveryRequestMenuItem;
